import * as Dialog from '@radix-ui/react-dialog';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { Prompt as PromptEntry } from '~api/som';

import { DetailedHTMLProps, Fragment, HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import Markdown from 'react-markdown';

import { ChevronRight16, Close16, Copy16, IngridAi24, Maximize16, Minimize16, More16 } from '@ingrid/iconography/react';
import { DropdownMenu, IconButton, PileBadge, Tooltip, styled } from '@ingrid/luterkort-react';

import { LoadingDot } from '~src/components/LoadingDot/LoadingDot';
import { useTranslation } from '~src/i18n/use-translation';
import { toHighlightedJson, tryParseJson } from '~src/utils/json';

import { useBookingRulePromptMutation } from '../../mutations';
import { chatSchema, useChatForm } from '../hooks';

const CHAT_HISTORY_LS_KEY = 'transport.cbm_ai_chat_history';

const MarkdownWrapper = styled('div', {
  flexDirection: 'column',
  display: 'flex',
  gap: '16px',

  '*:not(.monaco-editor *)': {
    lineHeight: '20px',
    fontSize: '14px',
  },

  'h1, h2, h3, h4, h5, h6': {
    margin: '8px 0 0 0',
  },

  'ol, ul': {
    flexDirection: 'column',
    padding: '0 0 0 22px',
    display: 'flex',
    gap: '4px',
    margin: 0,

    'ol, ul': {
      marginTop: '4px',
    },
  },

  pre: {
    background: 'var(--grey-e9)',
    borderRadius: '2px',
    maxHeight: '250px',
    overflow: 'scroll',
    padding: '8px',
    margin: 0,
  },

  ':not(pre) > code': {
    background: 'var(--grey-e9)',
    borderRadius: '6px',
    padding: '1px 6px',
  },

  code: {
    '.number': {
      color: '#098658',
    },
    '.key': {
      color: '#a31515',
    },
    '.string': {
      color: '#0451a5',
    },
    '.boolean': {
      color: '#0451a5',
    },
    '.null': {
      color: '#0451a5',
    },
  },

  p: {
    padding: 0,
    margin: 0,
  },

  hr: {
    borderTop: '1px solid var(--grey-de)',
    margin: '8px 0 0 0',
    padding: 0,
  },
});

const Content = styled(Dialog.Content, {
  '--chat-conv-max-height': '450px',
  '--chat-conv-min-height': '0px',
  '--chat-max-width': '460px',

  background: 'linear-gradient(90deg, #AD83E9 0%, #9C27B0 39.9%, #D481E3 72.6%, #732DD9 100%)',
  boxShadow: '0px 2px 12px 4px #00000026',
  maxWidth: 'var(--chat-max-width)',
  borderRadius: '4px',
  position: 'fixed',
  padding: '2px',
  width: '100%',
  bottom: '8px',
  left: '8px',
  zIndex: 10,

  '&:focus': {
    outline: 'none',
  },

  '&.large': {
    '--chat-conv-max-height': '750px',
    '--chat-conv-min-height': '450px',
    '--chat-max-width': '850px',
  },
});

const InnerContent = styled('div', {
  background: 'white',
  borderRadius: '3px',

  variants: {
    mode: {
      dark: {
        background: 'var(--grey-18)',
      },
    },
  },
});

const Toolbar = styled('div', {
  borderBottom: '1px solid var(--grey-e9)',
  alignItems: 'center',
  display: 'flex',
  padding: '16px',

  variants: {
    mode: {
      dark: {
        borderColor: '#333333',
      },
    },
  },
});

const ToolbarColumn = styled('div', {
  alignItems: 'center',
  display: 'flex',
  gap: '8px',
  flex: 1,
});

const Title = styled(Dialog.Title, {
  fontFamily: 'var(--universal)',
  fontWeight: 'bold',
  fontSize: '16px',
  padding: 0,
  margin: 0,
});

const Conversation = styled('div', {
  borderBottom: '1px solid var(--grey-e9)',
  maxHeight: 'var(--chat-conv-max-height)',
  minHeight: 'var(--chat-conv-min-height)',
  flexDirection: 'column',
  overflowY: 'scroll',
  padding: '16px',
  display: 'flex',
  gap: '8px',

  variants: {
    mode: {
      dark: {
        borderColor: '#333333',
      },
    },
  },
});

const Message = styled('div', {
  backgroundColor: 'var(--purple-01)',
  display: 'inline-block',
  whiteSpace: 'pre-wrap',
  borderRadius: '2px',
  marginLeft: 'auto',
  maxWidth: '380px',
  fontSize: '14px',
  padding: '12px',

  variants: {
    mode: {
      dark: {
        backgroundColor: 'var(--purple-02)',
        color: 'white',
      },
    },
  },
});

const Answer = styled('div', {
  backgroundColor: 'var(--grey-f5)',
  display: 'inline-block',
  borderRadius: '2px',
  maxWidth: '540px',
  padding: '12px',

  variants: {
    mode: {
      dark: {
        color: 'black',
      },
    },
  },
});

const Prompt = styled('form', {
  position: 'sticky',
  display: 'flex',
  padding: '16px',
  gap: '16px',

  textarea: {
    fontFamily: 'var(--universal)',
    background: 'transparent',
    fontSize: '14px',
    resize: 'none',
    padding: 0,
    border: 0,
    flex: 1,

    '&:focus': {
      outline: 'none',
    },
  },
});

interface Props {
  trigger: ReactNode;
}

function PreBlock({ children }: DetailedHTMLProps<HTMLAttributes<HTMLPreElement>, HTMLPreElement>) {
  const child = children as JSX.Element | undefined;

  const extractedCode = String(child?.props?.children);
  const extractedLang = String(child?.props?.className);

  const { t } = useTranslation();

  async function handleCopy() {
    await navigator.clipboard.writeText(extractedCode);
  }

  return (
    <div style={{ position: 'relative' }}>
      <pre>
        {extractedLang === 'language-json' ? (
          <code dangerouslySetInnerHTML={{ __html: toHighlightedJson(extractedCode) }} />
        ) : (
          <code>{children}</code>
        )}
      </pre>

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <Tooltip content={t('common.copyAriaLabel')}>
          <IconButton variant="quinary" size="sm" aria-label={t('common.copy')} onClick={handleCopy}>
            <Copy16 />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}

function ConversationEntry({ entry }: { entry: PromptEntry }) {
  const result = match(entry.role)
    .with('ASSISTANT', () => (
      <Answer>
        <MarkdownWrapper>
          <Markdown
            components={{
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              pre: ({ ref, ...props }) => <PreBlock {...props} />,
            }}
          >
            {entry.message}
          </Markdown>
        </MarkdownWrapper>
      </Answer>
    ))
    .with('USER', () => (
      <Message>
        <MarkdownWrapper>
          <p>{entry.message}</p>
        </MarkdownWrapper>
      </Message>
    ))
    .otherwise(() => new Error('invalid conversation entry'));

  if (result instanceof Error) {
    throw result;
  }

  return result;
}

export function ChatDialog({ trigger }: Props) {
  const [isLarge, setIsLarge] = useState(false);
  const [conversation, setConversation] = useState<PromptEntry[]>(
    () =>
      (tryParseJson(window.localStorage.getItem(CHAT_HISTORY_LS_KEY) ?? undefined, true) as
        | PromptEntry[]
        | undefined) ?? []
  );

  const { register, handleSubmit, formState, resetField } = useChatForm();
  const { t } = useTranslation();

  const mutation = useBookingRulePromptMutation();

  async function onSubmit(payload: z.infer<typeof chatSchema>) {
    const updatedConversation: PromptEntry[] = [...conversation, { message: payload.prompt, role: 'USER' }];
    setConversation(updatedConversation);
    resetField('prompt');

    try {
      const { data } = await mutation.mutateAsync({
        prompt_history: updatedConversation,
      });

      if (data.response) {
        const uc: PromptEntry[] = [...updatedConversation, data.response];
        setConversation(uc);
      }
    } catch (e) {
      const uc: PromptEntry[] = [
        ...updatedConversation,
        { role: 'ASSISTANT', message: t('bookingRules.chat.unavailable') },
      ];

      setConversation(uc);

      throw e;
    }
  }

  function handleClearChat() {
    window.localStorage.removeItem(CHAT_HISTORY_LS_KEY);
    setConversation([]);
  }

  function toggleResize() {
    setIsLarge(p => !p);
  }

  useEffect(() => {
    if (conversation.length > 0) {
      window.localStorage.setItem(CHAT_HISTORY_LS_KEY, JSON.stringify(conversation));
    }
  }, [conversation]);

  return (
    <Fragment>
      <Dialog.Root>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>

        <Dialog.Portal>
          <Content className={isLarge ? 'large' : ''}>
            <InnerContent>
              <Toolbar>
                <ToolbarColumn>
                  <IngridAi24 />
                  <Title>{t('common.ingrid-ai')}</Title>
                  <PileBadge variant="critical">Beta</PileBadge>
                </ToolbarColumn>

                <ToolbarColumn style={{ justifyContent: 'flex-end' }}>
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <IconButton size="sm" variant="quaternary" aria-label="More">
                        <More16 />
                      </IconButton>
                    </DropdownMenu.Trigger>

                    <DropdownMenu.Content>
                      <DropdownMenu.Item onClick={handleClearChat}>{t('bookingRules.chat.new')}</DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>

                  <IconButton size="sm" variant="quaternary" aria-label={t('common.resize')} onClick={toggleResize}>
                    {isLarge ? <Minimize16 /> : <Maximize16 />}
                  </IconButton>

                  <Dialog.Close asChild>
                    <IconButton size="sm" variant="quaternary" aria-label={t('common.close')}>
                      <Close16 />
                    </IconButton>
                  </Dialog.Close>
                </ToolbarColumn>
              </Toolbar>

              {conversation.length > 0 && (
                <Conversation>
                  {conversation.map((entry, index) => (
                    <ConversationEntry entry={entry} key={`chat-entry-${index}`} />
                  ))}

                  {mutation.isPending && <LoadingDot />}
                </Conversation>
              )}

              <Prompt onSubmit={handleSubmit(onSubmit)}>
                <textarea {...register('prompt')} placeholder={t('bookingRules.chat.placeholder')} autoFocus />

                <IconButton
                  disabled={!formState.isValid || mutation.isPending}
                  aria-label={t('common.send')}
                  variant="primary"
                  type="submit"
                  size="sm"
                >
                  <ChevronRight16 />
                </IconButton>
              </Prompt>
            </InnerContent>
          </Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Fragment>
  );
}
